import { useEffect } from 'react'
import { Button, Col, Empty, Input, Row, Space, Table, TableColumnType, TableProps } from 'antd'
import { useState } from 'react'
import VSpace from '../common/VSpace'
import { SorterResult } from 'antd/es/table/interface'
import { useDebouncedState } from '../../hooks/useDebouncedState'
import { useSavedState } from '../../hooks/useSavedState'
import { ConsultationsListItem } from './consultationsModel'
import { useConsultations } from './consultationsQueries'
import dayjs from 'dayjs'

type KeysOfType<T, TProperty> = { [k in keyof T]: T[k] extends TProperty ? k : never }[keyof T]

const stringSorter = (property: KeysOfType<ConsultationsListItem, string>) => (a: ConsultationsListItem, b: ConsultationsListItem): number => {
  return (a[property] ?? '').localeCompare(b[property] ?? '')
}

const ConsultationsList = () => {
  const { debouncedState: debouncedSearchText, state: searchText, setState: setSearchText } = useDebouncedState<string | undefined>(undefined, 200)

  const settingKey = (setting: string) => `consultations-list/${setting}}`
  const [sortColumn, setSortColumn] = useSavedState<string | undefined>(settingKey('sort-column'), undefined)
  const [sortOrder, setSortOrder] = useSavedState<'ascend' | 'descend' | null | undefined>(settingKey('sort-order'), undefined)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useSavedState(settingKey('page-size'), 10)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearchText, sortColumn, sortOrder])

  const consultations = useConsultations({
    sortColumn,
    sortOrder: sortOrder ?? undefined,
    search: debouncedSearchText,
    page,
    pageSize
  })

  const stringColumn = (property: KeysOfType<ConsultationsListItem, string>, title: string): TableColumnType<ConsultationsListItem> => {
    return {
      title,
      dataIndex: property,
      sorter: stringSorter(property),
      sortOrder: sortColumn === property ? sortOrder : undefined
    }
  }

  const createdOnColumn: TableColumnType<ConsultationsListItem>[] = [{
    title: 'Created On',
    dataIndex: 'createdUtc',
    render: (_, data) => {
      return dayjs(data.createdLocal).format('MMM D, YYYY')
    },
    sorter: (a, b) => a.createdUtc.localeCompare(b.createdUtc),
    sortOrder: sortColumn === 'createdUtc' ? sortOrder : undefined
  }]

  const scheduledOnColumn: TableColumnType<ConsultationsListItem>[] = [{
    title: 'Scheduled On',
    dataIndex: 'startUtc',
    render: (_, data) => {
      return dayjs(data.startLocal).format('MMM D, YYYY h:mm A')
    },
    sorter: (a, b) => a.startUtc.localeCompare(b.startUtc),
    sortOrder: sortColumn === 'startUtc' ? sortOrder : undefined
  }]

  const statusColumn: TableColumnType<ConsultationsListItem>[] = [{
    title: 'Status',
    dataIndex: 'status',
    render: (status: number) => {
      switch (status) {
        case 0: return 'Scheduled';
        case 1: return 'Pending';
        case 2: return 'Sold';
        case 3: return 'Lost';
        case 4: return 'Canceled';
        default: return '???';
      }
    },
    sorter: (a, b) => a.status - b.status,
    sortOrder: sortColumn === 'status' ? sortOrder : undefined
  }]


  const columns: TableColumnType<ConsultationsListItem>[] = [
    ...createdOnColumn,
    stringColumn('salesPerson', 'Sales Person'),
    stringColumn('customer', 'Customer'),
    stringColumn('formattedAddress', 'Address'),
    stringColumn('summary', 'Summary'),
    stringColumn('projectName', 'Project'),
    ...statusColumn,
    ...scheduledOnColumn,
  ]

  const onTableChange: TableProps<ConsultationsListItem>['onChange'] = (_pagination, _filters, sorter) => {
    const sorterResult = (sorter as SorterResult<ConsultationsListItem>)
    setSortColumn(sorterResult.field as string | undefined)
    setSortOrder(sorterResult.order)
  }

  const labelStyle: React.CSSProperties = {
    width: '100%',
  }
  const inputStyle: React.CSSProperties = {
    width: '100%',
    fontWeight: 'initial'
  }

  const anyFiltersSet = debouncedSearchText

  const clearFilters = () => {
    setSearchText(undefined)
  }

  return <VSpace size="large">
    <Row gutter={[16, 16]}>
      <Col xl={12} span={24}>
        <label style={labelStyle}>
          Search
          <Input.Search
            placeholder="Search consultations..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={inputStyle}
          />
        </label>
      </Col>
    </Row>
    <Table
      columns={columns}
      dataSource={consultations.data?.consultations ?? []}
      rowKey="id"
      showSorterTooltip={false}
      onChange={onTableChange}
      pagination={{
        pageSize,
        current: page,
        total: consultations.data?.totalRows ?? 0,
        onChange: (page, pageSize) => {
          setPage(page)
          setPageSize(pageSize)
        }
      }}
      onRow={(item) => {
        return {
          onClick: () => {
            window.location.href = `#!/customers#${item.url}`
          },
          style: {
            cursor: 'pointer'
          }
        }
      }}
      locale={{
        emptyText: <Empty description={<Space direction="vertical">
          <span>No Results</span>
          {anyFiltersSet && <Button type="primary" onClick={clearFilters}>Clear Filters</Button>}
        </Space>} />
      }}
      loading={consultations.isLoading}
    />
  </VSpace >
}

export default ConsultationsList
